import React, { useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, KeyRound, Copy, Loader2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { formatDateDMY, formatImporte } from '../../lib/utils';

const METODO = {
  portal: { label: 'Portal', cls: 'bg-violet-100 text-violet-700' },
  email: { label: 'Email', cls: 'bg-sky-100 text-sky-700' },
  ambos: { label: 'Portal + Email', cls: 'bg-amber-100 text-amber-800' },
};

const Copiable = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-2 py-1">
    <span className="text-xs text-slate-500 w-20 shrink-0">{label}</span>
    <span className="font-mono text-sm text-slate-800 truncate flex-1" data-testid={testId}>{value || '—'}</span>
    {value && (
      <button type="button" className="p-1 text-slate-400 hover:text-slate-700" title="Copiar"
        onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copiado`); }} data-testid={`${testId}-copiar`}>
        <Copy className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

const PortalAcceso = ({ contratoId, url, testId }) => {
  const [data, setData] = useState(null);
  const cargar = async () => {
    if (data) return data;
    try { const r = await api.get(`/facturacion/contratos/${contratoId}/portal`); setData(r.data); return r.data; }
    catch { toast.error('No se pudo leer el acceso al portal'); return null; }
  };
  const abrir = async () => {
    const d = await cargar();
    if (!d) return;
    if (!d.url) { toast.error('El contrato no tiene la dirección del portal cargada'); return; }
    window.open(/^https?:\/\//i.test(d.url) ? d.url : `https://${d.url}`, '_blank', 'noopener');
  };
  return (
    <span className="inline-flex items-center gap-0.5">
      <button type="button" onClick={abrir} disabled={!url} title={url ? `Abrir ${url}` : 'Sin dirección de portal cargada en el contrato'}
        className="p-1 rounded text-violet-600 hover:bg-violet-50 disabled:text-slate-300 disabled:hover:bg-transparent" data-testid={`${testId}-abrir`}>
        <ExternalLink className="w-3.5 h-3.5" />
      </button>
      <Popover onOpenChange={(o) => o && cargar()}>
        <PopoverTrigger asChild>
          <button type="button" title="Usuario y contraseña" className="p-1 rounded text-violet-600 hover:bg-violet-50" data-testid={`${testId}-credenciales`}>
            <KeyRound className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72" data-testid={`${testId}-popover`}>
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
            <div>
              <p className="text-sm font-semibold mb-1">{data.nombre || 'Portal'}</p>
              <Copiable label="Usuario" value={data.usuario} testId={`${testId}-usuario`} />
              <Copiable label="Contraseña" value={data.password} testId={`${testId}-password`} />
              {data.instrucciones && <p className="text-xs text-slate-500 mt-2 whitespace-pre-line">{data.instrucciones}</p>}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
};

export const MetodoFacturaBadge = ({ metodo, contratoId, portalUrl, testId }) => {
  const m = METODO[metodo];
  if (!m) return <span className="text-slate-300">-</span>;
  const badge = <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${m.cls}`} data-testid={testId}>{m.label}</span>;
  if (!contratoId || metodo === 'email') return badge;
  return <span className="inline-flex items-center gap-0.5">{badge}<PortalAcceso contratoId={contratoId} url={portalUrl} testId={`${testId}-portal`} /></span>;
};

const Fila = ({ label, children }) => (children === null || children === undefined || children === '' ? null : (
  <div className="flex gap-3 py-1 border-b border-slate-100 last:border-0">
    <span className="w-40 shrink-0 text-xs text-slate-500">{label}</span>
    <span className="text-sm text-slate-800 break-words min-w-0">{children}</span>
  </div>
));

export const ContratoInfoDialog = ({ item, onClose }) => {
  const c = item?.contrato_info || {};
  const emails = (c.emails_facturacion || []).filter(Boolean);
  const metodo = item?.facturacion_metodo;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="contrato-info-dialog">
        <DialogHeader>
          <DialogTitle>{item?.nombre_comercial || item?.inquilino}</DialogTitle>
          <DialogDescription>Unidad {item?.unidad_nombre}{c.numero_contrato ? ` · Contrato ${c.numero_contrato}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto">
          <Fila label="Razón social">{c.razon_social || item?.razon_social}</Fila>
          <Fila label="CUIT">{c.cuit || item?.cuit}</Fila>
          <Fila label="Condición IVA">{c.condicion_iva}</Fila>
          <Fila label="Vigencia">{c.fecha_inicio ? `${formatDateDMY(c.fecha_inicio)} al ${formatDateDMY(c.fecha_fin)}` : null}</Fila>
          <Fila label="Monto contrato">{c.monto_actual ? `${c.moneda || 'ARS'} ${formatImporte(c.monto_actual, 2)}` : null}</Fila>
          <Fila label="Ajuste">{c.indice_tipo ? `${String(c.indice_tipo).toUpperCase()} ${c.ajuste_frecuencia || ''}` : null}</Fila>
          <Fila label="Emisión / vencimiento">{c.dia_emision || c.dia_vencimiento ? `Día ${c.dia_emision || '-'} / día ${c.dia_vencimiento || '-'}` : null}</Fila>
          <Fila label="Orden de compra">{c.orden_compra}</Fila>
          <Fila label="Gestión de factura">{metodo ? <MetodoFacturaBadge metodo={metodo} /> : null}</Fila>
          <Fila label="Emails de facturación">{(emails.length ? emails : [c.email_facturacion || item?.email_facturacion].filter(Boolean)).join(', ') || null}</Fila>
          <Fila label="Portal">{c.proveedor_plataforma_nombre}</Fila>
          <Fila label="URL portal">{c.proveedor_plataforma_url ? (
            <a href={c.proveedor_plataforma_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="contrato-info-portal-link">
              {c.proveedor_plataforma_url} <ExternalLink className="w-3 h-3" />
            </a>) : null}</Fila>
          <Fila label="Usuario portal">{c.proveedor_plataforma_usuario}</Fila>
          <Fila label="Instrucciones portal">{c.proveedor_plataforma_instrucciones}</Fila>
          <Fila label="Contacto administrativo">{[c.contacto_admin_nombre, c.contacto_admin_email, c.contacto_admin_telefono].filter(Boolean).join(' · ') || null}</Fila>
        </div>
      </DialogContent>
    </Dialog>
  );
};
